import type { TrelloWindow } from '@trello/window-types';
import type { RpcModel } from './rpc.types';
import { AjaxRPC } from './AjaxRPC';
import { SocketRPC } from './SocketRPC';
import { Auth } from 'app/scripts/db/auth';
import BluebirdPromise from 'bluebird';
import _ from 'underscore';
import { Analytics } from '@trello/atlassian-analytics';
import { featureFlagClient } from '@trello/feature-flag-client';
import { getMilliseconds } from '@trello/time';
import { rpcEvents } from './rpcEvents';
import { ajaxEvents } from './ajaxEvents';
import { socketRPCEvents } from './socketRPCEvents';
import { webSocketsAvailable, webSocketState } from '@trello/web-sockets';
import { verificationUrls } from './verificationUrls';
import { sendVerificationRequests } from './sendVerificationRequests';
import { logConnectionInformation } from './logConnectionInformation';
import { connectionInformationState } from 'app/scripts/network/rpc/connectionInformationState';

declare const window: TrelloWindow;

const VERIFY_INTERVAL_MS = 20 * 1000;
const INVALID_MODEL_ERRORS = ['unauthorized', 'forbidden', 'not found'];

type SocketRPCInstance = InstanceType<typeof SocketRPC>;
type AjaxRPCInstance = InstanceType<typeof AjaxRPC>;
type RPCInstance<T extends SocketRPCInstance | AjaxRPCInstance> = T;

const randomWait = (wait: number, spread: number): number => {
  return Math.floor(wait + Math.random() * spread);
};
export class RPC {
  rpcSocket: SocketRPCInstance | null;
  rpcAJAX: AjaxRPCInstance | null;
  rpcCurrent:
    | RPCInstance<SocketRPCInstance>
    | RPCInstance<AjaxRPCInstance>
    | null;
  #currentEventBus: typeof ajaxEvents | typeof socketRPCEvents;
  currentSubscriptions: Record<string, RpcModel>;
  rpcConnectTime: number;
  socketRetryWaitTime: number;
  blockSocketReconnection: boolean;
  handledSubscriptionErrors: string[];
  _verifyTimeout?: number;
  activeSubscriptionCount: {
    [key: string]: number;
  } = {};

  // We want to immediately open up an rpc channel, so we use AJAX until the socket has connected
  // and proven that it can answer a ping. If the socket connects and answers quickly, we use
  // that for all future requests. If not, we just close it down and keep using AJAX. We defer
  // setting up anything that will require polling on the AJAX rpc until we either confirm or
  // give up on the socket rpc.
  constructor() {
    this.rpcSocket = this.rpcAJAX = this.rpcCurrent = null;
    this.currentSubscriptions = {};

    this.rpcConnectTime = 0;
    this.socketRetryWaitTime = 0;
    this.blockSocketReconnection = false;
    this.handledSubscriptionErrors = INVALID_MODEL_ERRORS;
    // Always start an RPC if we're logged in, so we can get our notifications
    this.handleLogin();

    setTimeout(() => {
      logConnectionInformation({
        eventName: 'verifySubscriptions',
        payload: { ...this.currentSubscriptions },
        source: 'rpc',
      });
      return this.verifySubscriptions();
    }, VERIFY_INTERVAL_MS);

    featureFlagClient.on<boolean>(
      'fep.disconnect_active_clients',
      false,
      (disconnectActiveClients) => {
        if (disconnectActiveClients === true) {
          this.blockSocketReconnection = true;
          this.rpcAJAX?.stopPolling?.();
        }
      },
    );
  }

  rpcReady() {
    if (!this.isUsingSocket()) {
      this.rpcAJAX?.poll?.();
    }

    rpcEvents.trigger('ready');

    this.resubscribe();

    rpcEvents.listenTo(rpcEvents, 'reconnect', () => {
      return this.resubscribe();
    });
  }

  resubscribe() {
    const result = [];
    for (const idModel in this.currentSubscriptions) {
      const subscription = this.currentSubscriptions[idModel];
      result.push(
        this.subscribe(subscription.modelType, idModel, subscription.tags),
      );
    }
    return result;
  }

  setActiveRPC(rpc: AjaxRPCInstance | SocketRPCInstance) {
    rpcEvents.stopListening(this.#currentEventBus);
    this.rpcCurrent = rpc;
    this.#currentEventBus =
      rpc instanceof AjaxRPC ? ajaxEvents : socketRPCEvents;

    ajaxEvents.listenTo(ajaxEvents, 'all', (eventName, payload) => {
      logConnectionInformation({
        eventName: eventName,
        payload: payload,
        source: 'ajax',
      });
    });

    socketRPCEvents.listenTo(socketRPCEvents, 'all', (eventName, payload) => {
      logConnectionInformation({
        eventName: eventName,
        payload: payload,
        source: 'socketRpc',
      });
    });

    rpcEvents.listenTo(this.#currentEventBus, 'all', (...args: unknown[]) => {
      // @ts-expect-error
      return rpcEvents.trigger(...Array.from(args || []));
    });
  }

  lazyCreateRPC() {
    if (
      this.blockSocketReconnection === true ||
      this.isUsingSocket() ||
      Date.now() - this.rpcConnectTime < this.socketRetryWaitTime
    ) {
      return;
    }

    const traceId = Analytics.startTask({
      taskName: 'create-session/socket',
      source: 'network:rpc',
    });

    this.rpcConnectTime = Date.now();
    this.socketRetryWaitTime = randomWait(
      getMilliseconds({ minutes: 20 }),
      getMilliseconds({ minutes: 10 }),
    );

    // Create a new socket instance to force new Socket creation
    this.rpcSocket = new SocketRPC(this);
    if (this.rpcAJAX) {
      this.rpcAJAX.stopPolling();
    } else {
      this.rpcAJAX = new AjaxRPC(this);
    }

    // Until the socket proves it works, you're on AJAX rpc.
    this.setActiveRPC(this.rpcAJAX);

    if (webSocketsAvailable()) {
      const hasExtendedSocketTimeout = featureFlagClient.get(
        'fep.extended-socket-timeouts',
        false,
      );
      // Sockets have a chance of working. They have 8s to answer a ping before we give up on 'em.
      const msPingWait = hasExtendedSocketTimeout ? 15000 : 8000;

      return this.rpcSocket.ping((err, result, pingedSocket) => {
        const pingElapsedTime = Date.now() - this.rpcConnectTime;
        if (!!err || pingElapsedTime > msPingWait) {
          // Socket took too long. Use AJAX.

          // by the time we get here, @rpcCurrent may already be pointing at
          // a different socket than the one we just pinged
          pingedSocket?.disconnect?.();
          pingedSocket?.destroy?.();

          this.rpcSocket = null;

          // err can be string | Error | null. If a string or Error then
          // this failed. If null then this failed b/c slow.
          const errorMessage = err instanceof Error ? err.message : err;

          Analytics.sendOperationalEvent({
            action: 'errored',
            actionSubject: 'socketPing',
            source: 'network:rpc',
            attributes: {
              taskId: traceId,
              status: errorMessage ? 'failed' : 'slow',
              message: errorMessage ? errorMessage : 'error: slow ping',
              pingElapsedTime,
            },
          });

          Analytics.taskFailed({
            taskName: 'create-session/socket',
            source: 'network:rpc',
            traceId,
            error: new Error('slow ping'),
          });

          this.rpcReady();
          setTimeout(() => {
            return this.lazyCreateRPC();
          }, this.socketRetryWaitTime);
        } else {
          // Yay, we have sockets!
          // @ts-expect-error im not sure how this is set by the time it gets here, but it is
          this.setActiveRPC(this.rpcSocket);
          this.rpcReady();
          Analytics.taskSucceeded({
            taskName: 'create-session/socket',
            source: 'network:rpc',
            traceId,
          });
        }
      });
    } else {
      // We do not have an acceptable socket transport. Use AJAX.
      Analytics.sendOperationalEvent({
        action: 'errored',
        actionSubject: 'unsupportedSocketConnection',
        source: 'network:rpc',
        attributes: {
          taskId: traceId,
        },
      });
      Analytics.taskFailed({
        taskName: 'create-session/socket',
        source: 'network:rpc',
        traceId,
        error: new Error('Unsupported socket connection'),
      });
      return this.rpcReady();
    }
  }

  handleLogin() {
    if (Auth.isLoggedIn()) {
      return this.lazyCreateRPC();
    }
  }

  unsubscribe(modelType: string, idModel: string) {
    logConnectionInformation({
      eventName: 'unsubscribe',
      source: 'rpc',
      payload: {
        modelType,
        idModel,
      },
    });
    if (featureFlagClient.get('fep.report-repeated-subscriptions', false)) {
      const key = `${modelType}:${idModel}`;
      this.activeSubscriptionCount[key] = this.decrementSubscriptionCount(key);
    }
    this.lazyCreateRPC();
    this.rpcCurrent?.unsubscribe?.(modelType, idModel);
    return delete this.currentSubscriptions[idModel];
  }

  _subscribeOrHandle(modelType: string, idModel: string, tags: string[]) {
    return BluebirdPromise.fromNode((next) => {
      return this.rpcCurrent?.subscribe?.(modelType, idModel, tags, next);
    }).catch((err) => {
      if (INVALID_MODEL_ERRORS.includes(err.message)) {
        this.handleInvalidSubscription(idModel);
        return rpcEvents.trigger(
          'subscription_invalid',
          modelType,
          idModel,
          tags,
        );
      }
    });
  }

  incrementSubscriptionCount(key: string) {
    const currentCount = this.activeSubscriptionCount[key];
    return currentCount ? currentCount + 1 : 1;
  }

  decrementSubscriptionCount(key: string) {
    const currentCount = this.activeSubscriptionCount[key];
    return currentCount ? currentCount - 1 : 0;
  }

  recordAndReportRepeatedSubscriptions(modelType: string, idModel: string) {
    if (featureFlagClient.get('fep.report-repeated-subscriptions', false)) {
      const key = `${modelType}:${idModel}`;
      this.activeSubscriptionCount[key] = this.incrementSubscriptionCount(key);

      // Check if we have any repeated active subscriptions. We expect to see values up to 1000 here, so we're only
      // reporting every time there is a subscription that is repeated in multiples of 100. This is to help avoid
      // huge volumes of operational events.
      const hasRepeatedSubscriptions = Object.values(
        this.activeSubscriptionCount,
      ).some(
        (subscriptionCount) =>
          subscriptionCount !== 0 && subscriptionCount % 100 === 0,
      );

      if (hasRepeatedSubscriptions) {
        Analytics.sendOperationalEvent({
          action: 'repeated',
          actionSubject: 'socketSubscribe',
          source: 'network:rpc',
          attributes: {
            rpcCurrent:
              this.rpcCurrent instanceof AjaxRPC ? 'AjaxRPC' : 'SocketRPC',
            webSocketState: webSocketState.value,
            activeSubscriptions: this.activeSubscriptionCount,
            // We deliberately exclude the "payload" of the event log to avoid the risk of sending user-generated
            // content to the analytics service.
            eventLog: connectionInformationState.value.eventLog.map(
              ({ eventName, timestamp, source }) => ({
                eventName,
                timestamp,
                source,
              }),
            ),
          },
        });
      }
    }
  }

  subscribe(modelType: string, idModel: string, tags: string[]) {
    let subscription;
    this.recordAndReportRepeatedSubscriptions(modelType, idModel);
    this.lazyCreateRPC();

    logConnectionInformation({
      eventName: 'subscribe',
      source: 'rpc',
      payload: {
        modelType,
        idModel,
        tags,
      },
    });

    if ((subscription = this.currentSubscriptions[idModel])) {
      subscription.tags = _.union(subscription.tags, tags);
    } else {
      this.currentSubscriptions[idModel] = {
        tags,
        ixLastUpdate: -1,
        modelType,
        idModel,
      };
    }

    return this._subscribeOrHandle(modelType, idModel, tags);
  }

  getIxLastUpdate(idModel: string) {
    return this.currentSubscriptions[idModel]
      ? this.currentSubscriptions[idModel].ixLastUpdate
      : undefined;
  }

  setIxLastUpdate(idModel: string, ixLastUpdate: number) {
    let entry;
    if ((entry = this.currentSubscriptions[idModel])) {
      return (entry.ixLastUpdate = ixLastUpdate);
    }
  }

  stop() {
    if (this.isUsingSocket()) {
      this.rpcSocket?.destroy?.();
    } else {
      this.rpcAJAX?.stopPolling?.();
    }
  }

  isUsingSocket() {
    return !!this.rpcCurrent && this.rpcCurrent === this.rpcSocket;
  }

  handleInvalidSubscription(idModelInvalid: string) {
    let subscription;
    if ((subscription = this.currentSubscriptions[idModelInvalid])) {
      const { modelType, idModel, tags } = subscription;

      this.unsubscribe(modelType, idModel);
      rpcEvents.trigger('invalidModel', modelType, idModel, tags);
    }
  }

  // This is meant to catch some tricky situations where we might have lost
  // access to something even though we received no websocket update informing
  // us.  For example, a public board or team that we're looking at may have
  // been deleted, or a team visible board that we aren't a member of might
  // have been changed to private.
  verifySubscriptions() {
    clearTimeout(this._verifyTimeout);
    const me = Auth.isLoggedIn() ? Auth.me() : undefined;

    const verifyData = verificationUrls(this.currentSubscriptions, me);

    return sendVerificationRequests({
      data: verifyData,
      onApiError: this.handleInvalidSubscription.bind(this),
    }).finally(() => {
      let timeout: number;
      return (timeout = this._verifyTimeout = window.setTimeout(() => {
        if (timeout === this._verifyTimeout) {
          return requestAnimationFrame(() => {
            return this.verifySubscriptions();
          });
        }
      }, VERIFY_INTERVAL_MS));
    });
  }
}

// eslint-disable-next-line @trello/no-module-logic
export const rpc = (window.rpc = new RPC());

// If we're unable to subscribe to our own member channel, something
// has gone wrong (possibly we deleted our account or our session was
// invalidated from another browser)
// eslint-disable-next-line @trello/no-module-logic
if (Auth.isLoggedIn()) {
  // eslint-disable-next-line @trello/no-module-logic
  rpcEvents.on('invalidModel', function (typeName: string, id: string) {
    if (typeName === 'Member' && Auth.isMe(id)) {
      return Auth.removeToken();
    }
  });
}
